/**
 * ModularDining
 * ------------------------------------------------------------
 * Handles desktop toggle dropdowns + mobile drawer selection
 * for Table Top, Base & Chair options on the modular dining product page.
 * ------------------------------------------------------------
 */
class ModularDining {
  constructor() {
    // Desktop containers (table top + base + chair all use same markup pattern)
    this.containers = document.querySelectorAll(
      ".headboard__container, .color__container, .chair__container"
    );

    // The bounding wrapper - dropdown box must never render outside this
    this.galleryBounds = document.querySelector(".media-gallery-desktop");

    // Mobile trigger elements (open the bottom drawer)
    this.mobileHeadboardSelector = document.querySelector(
      ".headboard__container__mobile"
    );
    this.mobileColorSelector = document.querySelector(
      ".color__container__mobile"
    );
    this.mobileChairSelector = document.querySelector(
      ".chair__container__mobile"
    );

    // JSON data injected in page (table_top list, base list, chairs list, current product data)
    this.upholsteredData = JSON.parse(
      document.querySelector("#modularDiningData").textContent
    );

    // Desktop dropdown insert containers
    this.headboardDataInsertContainer = document.querySelector(
      ".headboardDataContainer"
    );
    this.colorDataInsertContainer = document.querySelector(
      ".colorDataContainer"
    );
    this.chairDataInsertContainer = document.querySelector(
      ".chairDataContainer"
    );

    // Mobile drawer insert container (shared for table top, base & chair)
    this.drawerDataInsertContainer = document.querySelector(
      ".drawerDataContainer"
    );

    // Drawer + overlay elements (mobile bottom sheet)
    this.drawer = document.querySelector(".comboOffer-drawer-class");
    this.overlay = document.querySelector(".comboOffer-custom-overlay");
    this.drawerTitleEl = document.querySelector(".newModularBeds-heading");

    this.init();
  }

  /**
   * Entry point - binds all events and loads initial desktop data.
   */
  init() {
    // alert('Initialized')
    this.bindDesktopToggle();
    this.bindMobileTriggers();
    this.bindDrawerCloseButtons();

    // Load desktop lists on page start
    this.loadHeadboardData(this.upholsteredData.table_top);
    this.loadColorData(this.upholsteredData.base);
    this.loadChairData(this.upholsteredData.chairs);
  }

  // ==================================================
  // DESKTOP TOGGLE LOGIC
  // ==================================================
  bindDesktopToggle() {
    this.containers.forEach((container) => {
      const icon = container.querySelector(".animation_prent_pulse");
      const box = container.querySelector(".headboard__data__container");

      // Prevent clicks inside the box from closing it (bubbling stop)
      box.addEventListener("click", (event) => event.stopPropagation());

      // Toggle box open/close on icon click
      icon.addEventListener("click", (event) => {
        event.stopPropagation();

        const isOpen = box.style.display === "block";

        // Close all open boxes first (only one open at a time)
        document
          .querySelectorAll(".headboard__data__container")
          .forEach((el) => (el.style.display = "none"));

          document
            .querySelectorAll(
              ".headboard__container, .color__container, .chair__container"
            )
            .forEach((el) => {
              el.classList.remove("active");
            });

        if (!isOpen) {
          box.style.display = "block";
          // Reposition so it never spills outside the gallery
          this.positionBoxWithinGallery(box, container);


          // Auto-scroll the active item into view inside this box
          const scrollArea = box.querySelector(
            ".headboardDataContainer, .colorDataContainer, .chairDataContainer"
          );
          this.scrollActiveItemIntoView(scrollArea || box);
        } else {
          box.style.display = "none";
        }
      });
    });

    // Clicking anywhere outside closes all open dropdowns
    document.addEventListener("click", () => {
      document
        .querySelectorAll(".headboard__data__container")
        .forEach((el) => (el.style.display = "none"));
    });

    // Re-clamp position on resize (gallery width can change)
    window.addEventListener("resize", () => {
      document
        .querySelectorAll(".headboard__data__container")
        .forEach((el) => {
          if (el.style.display === "block") {
            const parentContainer = el.closest(
              ".headboard__container, .color__container, .chair__container"
            );
            this.positionBoxWithinGallery(el, parentContainer);
          }
        });
    });
  }


  /**
     * Positions the dropdown box beside its own plus icon.
     * ONLY decides LEFT or RIGHT side based on available horizontal space.
     * Vertical position is always fixed to the icon's top — no vertical
     * shifting, clamping, or flipping at all.
     */
  positionBoxWithinGallery(box, triggerContainer) {
    const icon = triggerContainer.querySelector(".animation_prent_pulse");
    if (!icon) return;

    this.clearOverflowClipping(triggerContainer);

    document
      .querySelectorAll(
        ".headboard__container, .color__container, .chair__container"
      )
      .forEach((el) => {
        el.classList.remove("active");
      });

      triggerContainer.classList.add("active");


    triggerContainer.style.zIndex = "2";
    box.style.zIndex = "5";
    box.style.position = "absolute";
    box.style.bottom = "auto";
    box.style.marginTop = "0";
    box.style.marginBottom = "0";

    const offsetParent = box.offsetParent || triggerContainer;
    const parentRect = offsetParent.getBoundingClientRect();
    const iconRect = icon.getBoundingClientRect();

    // Fix vertical position to icon's top — always, no exceptions
    box.style.top = `${iconRect.top - parentRect.top}px`;

    // Measure box width by placing it to the right first
    box.style.left = "100%";
    box.style.right = "auto";
    box.style.marginLeft = "10px";
    box.style.marginRight = "0";
    const boxRect = box.getBoundingClientRect();

    // Horizontal bounds = tighter of gallery edge & viewport edge
    const viewportRight = window.innerWidth - 8;
    const viewportLeft = 8;
    let boundRight = viewportRight;
    let boundLeft = viewportLeft;

    if (this.galleryBounds) {
      const galleryRect = this.galleryBounds.getBoundingClientRect();
      boundRight = Math.min(boundRight, galleryRect.right);
      boundLeft = Math.max(boundLeft, galleryRect.left);
    }

    const spaceOnRight = boundRight - iconRect.right;
    const spaceOnLeft = iconRect.left - boundLeft;
    const boxWidth = boxRect.width;

    if (spaceOnRight >= boxWidth || spaceOnRight >= spaceOnLeft) {
      // Enough room on the right (or right has more room than left)
      box.style.left = "100%";
      box.style.right = "auto";
      box.style.marginLeft = "10px";
      box.style.marginRight = "0";
    } else {
      // Not enough room on the right -> open to the left instead
      box.style.left = "auto";
      box.style.right = "100%";
      box.style.marginLeft = "0";
      box.style.marginRight = "10px";
    }
  }

  /**
   * Walks up from a starting element to this.galleryBounds and
   * forces overflow:visible on any ancestor that would clip the popup.
   * Also fixes html/body in case the clip is happening at document level.
   */
  clearOverflowClipping(startEl) {
    let el = startEl;
    while (el && el !== this.galleryBounds?.parentElement) {
      const style = window.getComputedStyle(el);
      if (
        style.overflow === "hidden" ||
        style.overflowX === "hidden" ||
        style.overflowY === "hidden"
      ) {
        el.style.overflow = "visible";
      }
      if (el === this.galleryBounds) break;
      el = el.parentElement;
    }

    // Document-level clip guard
    if (window.getComputedStyle(document.body).overflowX === "hidden") {
      document.body.style.overflowX = "visible";
    }
    if (window.getComputedStyle(document.documentElement).overflowX === "hidden") {
      document.documentElement.style.overflowX = "visible";
    }
  }

  /**
   * Scrolls the active item into view WITHIN the given scrollable
   * container only (does not scroll the whole page).
   */
  scrollActiveItemIntoView(container) {
    if (!container) return;
    const activeItem = container.querySelector('[data-active="true"]');
    if (!activeItem) return;

    // Use rAF so it runs after layout/positioning is finalized
    requestAnimationFrame(() => {
      const containerRect = container.getBoundingClientRect();
      const itemRect = activeItem.getBoundingClientRect();

      const offset =
        itemRect.top -
        containerRect.top -
        containerRect.height / 2 +
        itemRect.height / 2;

      container.scrollTop += offset;
    });
  }

  // ==================================================
  // MOBILE DRAWER TRIGGERS
  // ==================================================
  bindMobileTriggers() {
    this.mobileHeadboardSelector?.addEventListener("click", () => {
      const heading = this.mobileHeadboardSelector.getAttribute("data-title");
      this.displayDataInDrawer(this.upholsteredData.table_top, "tabletop");
      this.openDrawer(heading);
    });

    this.mobileColorSelector?.addEventListener("click", () => {
      const heading = this.mobileColorSelector.getAttribute("data-title");
      this.displayDataInDrawer(this.upholsteredData.base, "base");
      this.openDrawer(heading);
    });

    this.mobileChairSelector?.addEventListener("click", () => {
      const heading = this.mobileChairSelector.getAttribute("data-title");
      this.displayDataInDrawer(this.upholsteredData.chairs, "chair");
      this.openDrawer(heading);
    });
  }

  bindDrawerCloseButtons() {
    document
      .querySelectorAll(".comboOffer-close-drawer")
      .forEach((btn) => btn.addEventListener("click", () => this.closeDrawer()));
  }

  /**
   * Opens the mobile bottom drawer with given title.
   */
  openDrawer(title) {
    if (this.drawerTitleEl) this.drawerTitleEl.textContent = title;

    this.drawer?.setAttribute("aria-hidden", "false");
    this.drawer?.setAttribute("open", "");
    document.body.classList.add("body-scroll-stop");

    this.overlay?.classList.add("is-visible");
  }

  /**
   * Closes the mobile bottom drawer.
   */
  closeDrawer() {
    this.drawer?.removeAttribute("open");
    this.drawer?.removeAttribute("aria-hidden");
    document.body.classList.remove("body-scroll-stop");

    this.overlay?.classList.remove("is-visible");
  }

  // ==================================================
  // SHARED HELPER: Determine which title is "active"
  // (i.e. matches current product's selected table_top/base/chair)
  // ==================================================
  getActiveTitle(type) {
    const productData = this.upholsteredData.productData;

    if (type === "tabletop") {
      return productData.productTableTop?.trim().toLowerCase();
    }
    if (type === "base") {
      return productData.productBase?.trim().toLowerCase();
    }
    if (type === "chair") {
      return productData.productChairs?.trim().toLowerCase();
    }
    return "";
  }

  // ==================================================
  // SHARED HELPER: Build one item's HTML markup
  // ==================================================
  buildItemMarkup(item, index, isActive) {
    return `
      <div class="headboard__item"
          data-index="${index}"
          ${isActive ? 'data-active="true"' : ""}
          style="
            display:flex;align-items:center;gap:10px;
            margin-bottom:10px;padding:10px;border-radius:6px;
            cursor:pointer;
            background:${isActive ? "#ffe5e5" : "transparent"};
      ">
          <img src="${item.image}" width="100" height="50" style="object-fit:cover;border-radius:6px;width:100px;height:50px;">
          <span style="font-weight:${isActive ? "700" : "400"};"> ${item.title} </span>
      </div>
    `;
  }

  // ==================================================
  // SHARED HELPER: Bind click-to-redirect on rendered items
  // Active item => no redirect, others => redirect to item.url
  // ==================================================
  bindItemClicks(container, data, activeTitle) {
    const items = container.querySelectorAll(".headboard__item");

    items.forEach((item, index) => {
      item.addEventListener("click", () => {
        const itemData = data[index];
        const clickedTitle = itemData.title.trim().toLowerCase();

        // ❌ Active item clicked -> do nothing
        if (clickedTitle === activeTitle) return;

        // ✔ Different item clicked -> redirect
        window.location.href = itemData.url;
      });
    });
  }

  // ==================================================
  // MOBILE DRAWER RENDER (table top + base + chair all use this)
  // ==================================================
  displayDataInDrawer(data, type) {
    if (!this.drawerDataInsertContainer) return;

    this.drawerDataInsertContainer.innerHTML = "";

    const activeTitle = this.getActiveTitle(type);

    data.forEach((item, index) => {
      const isActive = item.title.trim().toLowerCase() === activeTitle;
      this.drawerDataInsertContainer.innerHTML += this.buildItemMarkup(
        item,
        index,
        isActive
      );
    });

    this.bindItemClicks(this.drawerDataInsertContainer, data, activeTitle);

    // Auto-scroll active item into view inside the drawer
    this.scrollActiveItemIntoView(this.drawerDataInsertContainer);
  }

  // ==================================================
  // DESKTOP TABLE TOP RENDER
  // ==================================================
  loadHeadboardData(data) {
    if (!this.headboardDataInsertContainer) return;

    const activeTitle = this.getActiveTitle("tabletop");

    this.headboardDataInsertContainer.innerHTML = "";

    data.forEach((item, index) => {
      const isActive = item.title.trim().toLowerCase() === activeTitle;
      this.headboardDataInsertContainer.innerHTML += this.buildItemMarkup(
        item,
        index,
        isActive
      );
    });

    this.bindItemClicks(this.headboardDataInsertContainer, data, activeTitle);
  }

  // ==================================================
  // DESKTOP BASE RENDER
  // ==================================================
  loadColorData(data) {
    if (!this.colorDataInsertContainer) return;

    const activeTitle = this.getActiveTitle("base");

    this.colorDataInsertContainer.innerHTML = "";

    data.forEach((item, index) => {
      const isActive = item.title.trim().toLowerCase() === activeTitle;
      this.colorDataInsertContainer.innerHTML += this.buildItemMarkup(
        item,
        index,
        isActive
      );
    });

    this.bindItemClicks(this.colorDataInsertContainer, data, activeTitle);
  }

  // ==================================================
  // DESKTOP CHAIR RENDER
  // ==================================================
  loadChairData(data) {
    if (!this.chairDataInsertContainer) return;

    const activeTitle = this.getActiveTitle("chair");

    this.chairDataInsertContainer.innerHTML = "";

    data.forEach((item, index) => {
      const isActive = item.title.trim().toLowerCase() === activeTitle;
      this.chairDataInsertContainer.innerHTML += this.buildItemMarkup(
        item,
        index,
        isActive
      );
    });

    this.bindItemClicks(this.chairDataInsertContainer, data, activeTitle);
  }
}

// ------------------------------------------------------------
// Initialize once DOM is ready
// ------------------------------------------------------------
document.addEventListener("DOMContentLoaded", function () {
  new ModularDining();
});